import { useEffect, useState } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import {
  Avatar,
  Box,
  ButtonBase,
  Paper,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material'
import {
  HourglassTopRounded as HourglassIcon,
  ScheduleRounded as ScheduleIcon,
  SchoolRounded as SchoolIcon,
  type SvgIconComponent,
} from '@mui/icons-material'
import { darken } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { useActiveLessonStore } from '@/stores/activeLessonStore'
import type { ActiveLesson } from '@/types'
import { ROUTES } from '@/utils/constants'
import { formatLessonClock } from '@/utils/date'
import { isActiveLessonBarHiddenPath } from './activeLessonBarRoutes'

type Translate = ReturnType<typeof useTranslation>['t']

/**
 * Где стоит плашка. `sidebar` — под логотипом в боковом меню, пока меню
 * открыто на экране (шире 1024px). `header` — строкой под мобильной шапкой, когда
 * меню уехало в выезжающую панель. Показывается ровно одна из двух.
 */
export type ActiveLessonBarPlacement = 'sidebar' | 'header'

/** Минутной точности счётчика хватает: тикаем локально, без запросов к API. */
const TICK_MS = 15_000
const MINUTE_MS = 60_000

/**
 * Затемнение цвета фазы для мелкого счётчика в светлой теме. Карточка — ссылка:
 * при наведении и фокусе под текст ложатся action.hover (8 %) и action.focus
 * (12 %). Для success #10B981 на светлом фоне 0,42 даёт #096B4A — 6,2:1 в покое,
 * 5,2:1 при наведении, 4,8:1 при фокусе. Стандартный dark (0,3) при фокусе давал
 * 3,6:1 — ниже AA для текста 12px. Цифры верны без ряби фокуса — у ссылки она
 * выключена (см. LINK_SX).
 */
const COUNTER_LIGHT_DARKEN = 0.42

/**
 * Фаза плашки: идёт; запланирован и ещё не начался; плановое начало прошло,
 * а учитель урок не стартовал.
 */
type BarPhase = 'in_progress' | 'upcoming' | 'waiting'

const PHASE_VIEW: Record<BarPhase, { color: 'success' | 'info' | 'warning'; Icon: SvgIconComponent }> = {
  in_progress: { color: 'success', Icon: SchoolIcon },
  upcoming: { color: 'info', Icon: ScheduleIcon },
  waiting: { color: 'warning', Icon: HourglassIcon },
}

/**
 * Обёртка по месту. Плашка стоит в потоке каркаса и ничего не перекрывает. В меню
 * — под логотипом с отступами как у пунктов меню. В шапке — вторая строка на всю
 * ширину (шапка переносит ряд, см. `.dashboard-header-mobile` в dashboard.css);
 * сама шапка на широком экране скрыта. Какая из двух видна на узком экране, решает
 * dashboard.css по классу `active-lesson-bar--sidebar`: в меню — только пока
 * выезжающая панель открыта (шапку каркас тогда рисует без плашки).
 * Отступы в пикселях: шкала theme.spacing в проекте своя, не кратная 8.
 */
const PLACEMENT_SX: Record<ActiveLessonBarPlacement, SxProps<Theme>> = {
  sidebar: {
    px: '16px',
    pt: '16px',
    pb: '8px',
    flexShrink: 0,
  },
  header: {
    flexBasis: '100%',
    minWidth: 0,
  },
}

/**
 * Фокус с клавиатуры — фон action.focus и обводка primary внутри карточки, без
 * ряби (focusRipple у ButtonBase не включаем): слой ряби — currentColor с
 * прозрачностью 30 % поверх текста — опускал контраст счётчика при фокусе ниже
 * AA. Обводка внутрь (outlineOffset −2px): снаружи её обрезал бы overflow: hidden
 * у Paper.
 */
const LINK_SX: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '12px',
  width: '100%',
  minWidth: 0,
  px: '12px',
  py: '10px',
  textAlign: 'left',
  color: 'inherit',
  textDecoration: 'none',
  borderRadius: 'inherit',
  '&:hover': { bgcolor: 'action.hover' },
  '&.Mui-focusVisible': {
    bgcolor: 'action.focus',
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: '-2px',
  },
}

/**
 * Ширина разделителя между частями второй строки («с кем · N участников · время») —
 * около пробела, точки и пробела шрифтом body2. На столько же ряд второй строки
 * сдвинут влево: разделитель в начале перенесённой строки срезается.
 */
const DETAILS_SEPARATOR_PX = 12

/**
 * «С кем» — первая часть второй строки, базис по ширине текста (auto). Перенос в ряду
 * с flexWrap браузер решает по базису: число участников и время уходят на следующую
 * строку раньше, чем имя начнёт сжиматься. Многоточие — только если имя не помещается
 * в строку целиком. Отступ слева компенсирует сдвиг ряда на −DETAILS_SEPARATOR_PX.
 */
const DETAILS_WHO_SX: SxProps<Theme> = {
  flex: '0 1 auto',
  minWidth: 0,
  ml: `${DETAILS_SEPARATOR_PX}px`,
}

function resolvePhase(lesson: ActiveLesson, now: number): BarPhase {
  if (lesson.status === 'in_progress') return 'in_progress'
  return Date.parse(lesson.planned_start_at) > now ? 'upcoming' : 'waiting'
}

/** Целые минуты между моментами; битая дата или часы клиента «в прошлом» дают 0. */
function minutesBetween(fromMs: number, toMs: number, round: (value: number) => number): number {
  const minutes = round((toMs - fromMs) / MINUTE_MS)
  return Number.isFinite(minutes) ? Math.max(0, minutes) : 0
}

/** «25 минут», «1 час», «1 час 20 минут» — от 60 минут переходим на часы. */
function formatDuration(t: Translate, totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours === 0) return t('lessons.activeBar.minutes', { count: minutes })
  if (minutes === 0) return t('lessons.activeBar.hours', { count: hours })
  return t('lessons.activeBar.hoursMinutes', {
    hours: t('lessons.activeBar.hours', { count: hours }),
    minutes: t('lessons.activeBar.minutes', { count: minutes }),
  })
}

/**
 * С кем урок. Учителю — ученик или группа из title; ученику индивидуального
 * урока — учитель, группового — группа.
 */
function counterpartLabel(t: Translate, lesson: ActiveLesson): string {
  if (lesson.viewer_role === 'teacher') {
    return lesson.title ?? lesson.participants[0] ?? t('lessons.detailsTitle', { id: lesson.id })
  }
  if (lesson.lesson_scope === 'group') {
    return lesson.title ?? lesson.teacher.name
  }
  return lesson.teacher.name
}

/** Текущее время для счётчика: интервал плюс пересчёт при возврате на вкладку. */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const tick = () => setNow(Date.now())
    const intervalId = window.setInterval(tick, TICK_MS)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') tick()
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => {
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [])

  return now
}

interface ActiveLessonBarCardProps {
  lesson: ActiveLesson
  placement: ActiveLessonBarPlacement
}

function ActiveLessonBarCard({ lesson, placement }: ActiveLessonBarCardProps) {
  const { t } = useTranslation()
  const now = useNow()

  const phase = resolvePhase(lesson, now)
  const { color, Icon } = PHASE_VIEW[phase]

  let headline: string
  let counter: string | null = null
  let timeLabel: string

  if (phase === 'in_progress') {
    const startedAt = lesson.actual_start_at ?? lesson.planned_start_at
    const elapsed = minutesBetween(Date.parse(startedAt), now, Math.floor)
    headline = t('lessons.activeBar.inProgress')
    counter =
      elapsed < 1
        ? t('lessons.activeBar.elapsedLessThanMinute')
        : t('lessons.activeBar.elapsed', { duration: formatDuration(t, elapsed) })
    timeLabel = t('lessons.activeBar.startedAt', { time: formatLessonClock(startedAt) })
  } else {
    timeLabel = t('lessons.activeBar.startsAt', { time: formatLessonClock(lesson.planned_start_at) })
    if (phase === 'upcoming') {
      // Вверх: до начала 30 секунд — это «через 1 минуту», а не «через 0»
      const left = Math.max(1, minutesBetween(now, Date.parse(lesson.planned_start_at), Math.ceil))
      headline = t('lessons.activeBar.startsIn', { duration: formatDuration(t, left) })
    } else {
      headline = t('lessons.activeBar.waitingForStart')
    }
  }

  const who = counterpartLabel(t, lesson)
  // Число участников группового урока видят и учитель, и ученик
  const participants =
    lesson.lesson_scope === 'group'
      ? t('lessons.activeBar.participants', { count: lesson.participants_count })
      : null
  const details = participants
    ? t('lessons.activeBar.detailsWithParticipants', { who, participants, time: timeLabel })
    : t('lessons.activeBar.details', { who, time: timeLabel })
  // Часть второй строки после «с кем»: разделитель рисуется слева, в отступе части
  const detailsPartSx: SxProps<Theme> = {
    position: 'relative',
    flexShrink: 0,
    maxWidth: '100%',
    pl: `${DETAILS_SEPARATOR_PX}px`,
    '&::before': {
      content: JSON.stringify(t('lessons.activeBar.detailsSeparator')),
      position: 'absolute',
      left: 0,
      width: `${DETAILS_SEPARATOR_PX}px`,
      textAlign: 'center',
    },
  }

  const ariaLabel = counter
    ? t('lessons.activeBar.openAriaWithCounter', { headline, counter, details })
    : t('lessons.activeBar.openAria', { headline, details })

  return (
    <Box className={`active-lesson-bar active-lesson-bar--${placement}`} sx={PLACEMENT_SX[placement]}>
      <Paper
        variant="outlined"
        data-testid="active-lesson-bar"
        data-placement={placement}
        sx={{ overflow: 'hidden', borderColor: `${color}.main` }}
      >
        <ButtonBase component={RouterLink} to={ROUTES.LESSON_DETAILS(lesson.id)} aria-label={ariaLabel} sx={LINK_SX}>
          <Avatar
            aria-hidden
            sx={{ width: 32, height: 32, bgcolor: `${color}.main`, color: `${color}.contrastText` }}
          >
            <Icon fontSize="small" />
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {/* Заголовок и счётчик не отнимают ширину друг у друга: не помещаются в одну
                строку — счётчик переносится под заголовок (flexWrap), как в узком меню.
                Заголовок сам переносится по словам: в меню шириной 260px «Начнётся через
                12 минут» в строку не влезает, а многоточие съело бы само время. */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                columnGap: 2,
                minWidth: 0,
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} sx={{ minWidth: 0, maxWidth: '100%', overflowWrap: 'anywhere' }}>
                {headline}
              </Typography>
              {counter && (
                <Typography
                  variant="caption"
                  fontWeight={600}
                  noWrap
                  sx={{
                    minWidth: 0,
                    maxWidth: '100%',
                    // Мелкий цветной текст: в светлой теме main (зелёный success) на светлом
                    // фоне даёт контраст около 2,4:1 — затемняем с запасом на наведение и
                    // фокус (COUNTER_LIGHT_DARKEN). В тёмной теме затемнённый цвет на тёмном
                    // фоне сам терял бы контраст, там берём main.
                    color: (theme) =>
                      theme.palette.mode === 'light'
                        ? darken(theme.palette[color].main, COUNTER_LIGHT_DARKEN)
                        : theme.palette[color].main,
                  }}
                >
                  {counter}
                </Typography>
              )}
            </Box>
            {/* Вторая строка — «с кем», число участников группового урока и время отдельными
                частями через разделитель. Не помещаются в строку — число участников и время
                целиком переносятся ниже (flexWrap). Разделитель стоит перед частью, в начале
                перенесённой строки он уходит за левый край ряда и срезается (ряд сдвинут на
                −DETAILS_SEPARATOR_PX, у обёртки overflow: hidden). */}
            <Box sx={{ overflow: 'hidden' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'baseline',
                  ml: `-${DETAILS_SEPARATOR_PX}px`,
                }}
              >
                <Typography component="span" variant="body2" color="text.secondary" noWrap sx={DETAILS_WHO_SX}>
                  {who}
                </Typography>
                {participants && (
                  <Typography component="span" variant="body2" color="text.secondary" noWrap sx={detailsPartSx}>
                    {participants}
                  </Typography>
                )}
                <Typography component="span" variant="body2" color="text.secondary" noWrap sx={detailsPartSx}>
                  {timeLabel}
                </Typography>
              </Box>
            </Box>
          </Box>
        </ButtonBase>
      </Paper>
    </Box>
  )
}

interface ActiveLessonBarProps {
  placement: ActiveLessonBarPlacement
}

/**
 * Плашка текущего урока в каркасе кабинета: под логотипом в боковом меню, а на
 * узком экране — строкой под мобильной шапкой. Стоит в потоке и ничего не
 * перекрывает. Клик открывает урок. Пока ответа нет или урока нет — ничего не
 * рисует, поэтому пустой ответ не мигает.
 *
 * На страницах урока и прохождения (activeLessonBarRoutes.ts) не рисуется, но стор
 * урока не трогает: при уходе оттуда плашка сразу на месте.
 */
export function ActiveLessonBar({ placement }: ActiveLessonBarProps) {
  const lesson = useActiveLessonStore((state) => state.lesson)
  const { pathname } = useLocation()
  if (!lesson || isActiveLessonBarHiddenPath(pathname)) return null
  return <ActiveLessonBarCard lesson={lesson} placement={placement} />
}
